import {
  type Language,
  LANGUAGE_MAPPING,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
} from "Translations/languageConfig";
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from "Utils/Storage/LocalStorage/SecureLocalStorage";

/**
 * Detects the user's preferred language from browser settings
 * @returns The detected language or null if no supported language is found
 */
export const detectDeviceLanguage = (): Language | null => {
  // Check if navigator.languages is available (most modern browsers)
  if (navigator.languages && navigator.languages.length > 0) {
    for (const locale of navigator.languages) {
      const language = LANGUAGE_MAPPING[locale];
      if (language) {
        return language;
      }
    }
  }

  // Fallback to navigator.language (older browsers)
  if (navigator.language) {
    const language = LANGUAGE_MAPPING[navigator.language];
    if (language) {
      return language;
    }
  }

  // Fallback to navigator.userLanguage (IE)
  if ((navigator as any).userLanguage) {
    const language = LANGUAGE_MAPPING[(navigator as any).userLanguage];
    if (language) {
      return language;
    }
  }

  return null;
};

/**
 * Gets the initial language for the app
 * Priority: localStorage > device language > default (cs)
 */
export const getInitialLanguage = (): Language => {
  // First, check if user has explicitly set a language
  const stored = getLocalStorageItem(LANGUAGE_STORAGE_KEY) as Language;
  if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
    return stored;
  }

  // If no stored preference, detect from device
  const deviceLanguage = detectDeviceLanguage();
  if (deviceLanguage) {
    return deviceLanguage;
  }

  // Fallback to Czech
  return "cs";
};

/**
 * Initializes the app language based on device settings
 * This function should be called early in the app initialization
 */
export const initializeAppLanguage = (): Language => {
  const initialLanguage = getInitialLanguage();

  // Store the detected language if it's different from what's stored
  const stored = getLocalStorageItem(LANGUAGE_STORAGE_KEY);
  if (!stored || stored !== initialLanguage) {
    setLocalStorageItem(LANGUAGE_STORAGE_KEY, initialLanguage);
  }

  return initialLanguage;
};
